import React, { useEffect, useState } from "react";
import API from "../../API";

interface Metric {
  label: string;
  count: number;
}

interface MonthPoint {
  month: string;
  count: number;
}

interface StatsData {
  year: number | null;
  available_years: number[];
  total_households: number;
  completed_orders: number;
  pending_orders: number;
  completion_rate: number;
  total_people: number;
  total_adults: number;
  total_children: number;
  average_household_size: number;
  orders_last_7_days: number;
  orders_last_30_days: number;
  orders_this_year: number;
  total_contact_notes: number;
  top_countries: Metric[];
  orders_by_month: MonthPoint[];
}

const StatCard: React.FC<{ label: string; value: React.ReactNode; sub?: string }> = ({
  label,
  value,
  sub,
}) => (
  <div className="stat_card">
    <div className="stat_card_value">{value}</div>
    <div className="stat_card_label">{label}</div>
    {sub && <div className="stat_card_sub">{sub}</div>}
  </div>
);

const BarList: React.FC<{ title: string; data: Metric[] }> = ({ title, data }) => {
  const max = data.reduce((m, d) => Math.max(m, d.count), 0) || 1;

  return (
    <div className="stat_panel">
      <h3 className="stat_panel_title">{title}</h3>
      {data.length === 0 ? (
        <div className="stat_empty">No data yet</div>
      ) : (
        <ul className="bar_list">
          {data.map((d) => (
            <li className="bar_row" key={d.label}>
              <span className="bar_label" title={d.label}>
                {d.label}
              </span>
              <span className="bar_track">
                <span
                  className="bar_fill"
                  style={{ width: `${(d.count / max) * 100}%` }}
                />
              </span>
              <span className="bar_count">{d.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const currentYear = new Date().getFullYear();

const Stats: React.FC = () => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [year, setYear] = useState<number | "all">(currentYear);

  useEffect(() => {
    setError(false);
    API.fetchStats(year)
      .then((data) => setStats(data))
      .catch(() => setError(true));
  }, [year]);

  if (error) {
    return <div className="stats_message">Unable to load stats.</div>;
  }

  if (!stats) {
    return <div className="stats_message">Loading stats…</div>;
  }

  // Always offer the current year, even before its first order comes in.
  const years = Array.from(new Set([currentYear, ...stats.available_years])).sort(
    (a, b) => b - a
  );
  const isAllTime = year === "all";
  // Recent activity only means something for all time or the current year.
  const showRecent = isAllTime || year === currentYear;

  return (
    <div className="stats_container">
      <div className="stats_header">
        <h2 className="stats_heading">
          Dashboard{isAllTime ? " — All Time" : ` — ${year}`}
        </h2>
        <select
          className="stats_year_select"
          aria-label="Year"
          value={String(year)}
          onChange={(e) =>
            setYear(e.target.value === "all" ? "all" : Number(e.target.value))
          }
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
          <option value="all">All time</option>
        </select>
      </div>

      <div className="stat_card_grid">
        <StatCard label="Total Households" value={stats.total_households} />
        <StatCard label="People Served" value={stats.total_people} />
        <StatCard
          label="Adults / Children"
          value={`${stats.total_adults} / ${stats.total_children}`}
        />
        <StatCard
          label="Avg Household Size"
          value={stats.average_household_size}
        />
        <StatCard
          label="Completed Orders"
          value={stats.completed_orders}
          sub={`${stats.completion_rate}% completion rate`}
        />
        <StatCard label="Pending Orders" value={stats.pending_orders} />
        <StatCard label="Contact Notes" value={stats.total_contact_notes} />
        {isAllTime && (
          <StatCard label="Orders This Year" value={stats.orders_this_year} />
        )}
      </div>

      {showRecent && (
        <>
          <h3 className="stats_subheading">Recent Activity</h3>
          <div className="stat_card_grid">
            <StatCard label="Last 7 Days" value={stats.orders_last_7_days} sub="new orders" />
            <StatCard label="Last 30 Days" value={stats.orders_last_30_days} sub="new orders" />
          </div>
        </>
      )}

      <BarList
        title={isAllTime ? "Orders by Month (last 6 months)" : `Orders by Month — ${year}`}
        data={stats.orders_by_month.map((m) => ({ label: m.month, count: m.count }))} />

      <div className="stat_panel_grid">
        <BarList title="Top Countries of Origin" data={stats.top_countries} />
      </div>
    </div>
  );
};

export default Stats;
